import React from "react";
//#region types
import { MonthSliderProps } from "models/propTypes/wintFormPropsTypes";
import { IconDefinitionConstants } from "utils/constants";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Grid, IconButton, Typography } from "@mui/material";
import Icon from "components/common/icon";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const MonthSlider: React.FC<MonthSliderProps> = (props) => {
  const { value, shortFormat = true, shortAdjacentFormat = true, showYear = false, disabled } = props;

  const displayDate = value ?? new Date();
  const currentYear = new Date().getFullYear();
  const minMonth = props.minMonth ?? new Date(currentYear, 0, 1);
  const maxMonth = props.maxMonth ?? new Date(currentYear, 11, 1);

  const isAtMin =
    displayDate.getFullYear() === minMonth.getFullYear() && displayDate.getMonth() === minMonth.getMonth();
  const isAtMax =
    displayDate.getFullYear() === maxMonth.getFullYear() && displayDate.getMonth() === maxMonth.getMonth();

  const navigateLeft = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isAtMin || disabled) return;
    props.setValue(new Date(displayDate.getFullYear(), displayDate.getMonth() - 1, 1), true);
  };

  const navigateRight = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isAtMax || disabled) return;
    props.setValue(new Date(displayDate.getFullYear(), displayDate.getMonth() + 1, 1), true);
  };

  const getMonthName = (date: Date, short: boolean = shortFormat): string => {
    const monthNumber = date.getMonth() + 1;
    const monthKey = short
      ? (TranslationKeys.Common as Record<string, string>)[`MonthShort_${monthNumber}`]
      : (TranslationKeys.Common as Record<string, string>)[`Month_${monthNumber}`];
    const monthName = LocalizationHelperInstance.getFormattedMessage(monthKey);
    return showYear ? `${monthName} ${date.getFullYear()}` : monthName;
  };

  const prevDate = new Date(displayDate.getFullYear(), displayDate.getMonth() - 1, 1);
  const nextDate = new Date(displayDate.getFullYear(), displayDate.getMonth() + 1, 1);

  return (
    <Grid container alignItems="center" justifyContent="space-between" wrap="nowrap" className={clsx(cssExports.monthSlider, props.className)}>
      <Grid item display="flex" alignItems="center">
        <IconButton className={cssExports.monthSliderArrow} size="small" onClick={navigateLeft} disabled={disabled || isAtMin} style={isAtMin ? { visibility: "hidden" } : undefined}>
          <Icon icon={IconDefinitionConstants.WChevronSmallLeft} />
        </IconButton>
        {!isAtMin && <Typography className={cssExports.monthSliderAdjacentMonth}>{getMonthName(prevDate, shortAdjacentFormat)}</Typography>}
      </Grid>
      <Grid item>
        <Typography className={cssExports.monthSliderCurrentMonth}>{getMonthName(displayDate)}</Typography>
      </Grid>
      <Grid item display="flex" alignItems="center">
        {!isAtMax && <Typography className={cssExports.monthSliderAdjacentMonth}>{getMonthName(nextDate, shortAdjacentFormat)}</Typography>}
        <IconButton className={cssExports.monthSliderArrow} size="small" onClick={navigateRight} disabled={disabled || isAtMax} style={isAtMax ? { visibility: "hidden" } : undefined}>
          <Icon icon={IconDefinitionConstants.WChevronSmallRight} />
        </IconButton>
      </Grid>
    </Grid>
  );
};

export default MonthSlider;
